import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowUpRight, ArrowCounterClockwise, Columns, Eraser, Gauge, SidebarSimple } from '@phosphor-icons/react';
import { useRoomData } from '../../hooks/useRoomData';
import type { LessonPreviewAction, LessonPreviewHandle } from '../LessonPreview';
import { ChapteredMatch } from './ChapteredMatch';
import { COLOR_HEX, DRAW_COLORS, type DrawingTool } from '../videoEditor/editorControlValues';
import { RoomMatchDrawLayer } from './RoomMatchDrawLayer';
import { AddComparisonSourceModal } from '../AddComparisonSourceModal';
import type { ClipSource } from '../ClipEditorModal';
import type { DrawColor } from '../VideoCanvasOverlay';
import { useToast } from '../../hooks/useToast';
import type { LiveChapter } from '../../types/liveCall';
import type { NotableSegment } from '../../types/videoEdit';
import {
  followState, HEARTBEAT_MS, isMatchMessage, msg, nextLatency, scrubHoldsState, scrubSend,
  type MatchControl, type MatchMessage, type MatchStroke, type Panel, type PanelSource, type PanelState,
} from '../../utils/roomMatchSync';
import { MOVED_CHIP_MS, movedChip } from '../../utils/matchControls';
import { chapterAt, statesNow, type MatchSessionState } from '../../utils/matchSession';
import { sideBySideClip } from '../../utils/liveChapters';
import { saveMatchSession } from '../../services/liveCallBookings';
import { logger } from '../../utils/logger';
import { corsKnown, probeCors } from '../../services/callRecording';

/**
 * The match video in the call, the same for both (job 2 of 29 October).
 *
 * The player is the PWA's async lesson player (LessonPreview, decision 103):
 * its YouTube-style bar, frame-accurate scrubbing both ways, speed and
 * chapters. Never a second implementation. Side by side, each panel is one
 * of them, with its own bar.
 *
 * Anyone moves it; there is no driver and no lock (decision 100). The
 * coach's player is the source: it sends its state at 10 Hz and each move
 * as it happens. The player's moves go to it as 'ctl' and come back in that
 * state; they show on the player's own screen at once, as LessonPreview
 * applied them. Each side sees "Ana moved to 4:12" when the other moves it.
 * A scrub runs through the other side's player as its own scrub, so it
 * behaves exactly as on the async lesson page. Otherwise the follower only
 * ever seeks (roomMatchSync.followState): a scrub back
 * shows each frame and stops there; it never plays to catch up.
 *
 * The match is session state too (decision 130): the coach's player writes
 * open, each panel's position and the chapter on the call's session, and a
 * side that joins opens where the session says (`initial`) before anything
 * else; the coach sends nothing until it stands there, so the other side
 * never sees it jump.
 */

/** The coach's player writes the session's state on each move and this often while open. */
const SESSION_WRITE_MS = 5000;

/** After a move of its own, the player's panel ignores the coach's state this long, until it carries the move. */
const OWN_MOVE_MS = 700;

export function RoomMatchPlayer({
  isCoach, videoUrl, playerName, otherFirst, coachFirst, chapters, onChaptersChange, phone = false, tools = true, hideSecond = false, onShape, stageFullscreen, chromeHidden = false, session, initial, onRestored, cameras,
}: {
  isCoach: boolean;
  videoUrl: string;
  playerName?: string;
  /** The other person's first name: "Ana moved to 4:12". */
  otherFirst: string;
  /** The coach's first name: "<name>'s saved moments" (decision 128). */
  coachFirst?: string;
  /** The lesson's chapters, from the booking, live (decision 101). */
  chapters?: LiveChapter[] | null;
  /** The coach's new list, to save on the booking. */
  onChaptersChange?: (next: LiveChapter[]) => void;
  /** A phone: the video fills the width, panels stacked, nothing above it. Also the side column's tile while a share has the stage (144). */
  phone?: boolean;
  /** The coach's tools rail; not in the side column's small tile while a share has the stage (144). */
  tools?: boolean;
  /** A side-by-side's second panel hidden and paused (job 2): a phone's small column tile has room for one. */
  hideSecond?: boolean;
  /** The match's shape for the stage's layout (decision 162): its video's aspect, and whether a side-by-side is open. */
  onShape?: (s: { aspect: number | null; sideBySide: boolean }) => void;
  /** The call's own full screen for the match (162): never the system player. */
  stageFullscreen?: { on: boolean; toggle: () => void };
  /** A phone's controls hidden after 3 s without a touch (162): the tools rail goes with them. */
  chromeHidden?: boolean;
  /** The call's session, which the coach's player writes the match's state on, and who writes it. */
  session?: { bookingId: string; sessionId: string; by: string };
  /** The session's state as this side joined: where to open. */
  initial?: MatchSessionState | null;
  /** The initial state is applied (and spent). */
  onRestored?: () => void;
  /** Hide cameras (decision 131): a small control at the stage's top right, beside the tiles, not on the bar. */
  cameras?: { hidden: boolean; onToggle: () => void };
}) {
  const { sendData, onData, onDataChannelOpen } = useRoomData();
  const { showError } = useToast();
  const refs = [useRef<LessonPreviewHandle>(null), useRef<LessonPreviewHandle>(null)];
  const [panels, setPanels] = useState<PanelSource[]>([{ url: videoUrl, label: playerName ?? 'Match' }]);
  // The player's panels load with CORS where their source allows it, so the call's recording can
  // draw the match as the player sees it (hooks/useCallRecording). The coach's never need to.
  const [corsOk, setCorsOk] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (isCoach) return;
    let alive = true;
    for (const { url } of panels) {
      if (corsKnown(url) !== undefined) continue;
      void probeCors(url).then((ok) => { if (alive) setCorsOk((m) => ({ ...m, [url]: ok })); });
    }
    return () => { alive = false; };
  }, [isCoach, panels]);
  const loadsWithCors = (url: string) => !isCoach && (corsKnown(url) ?? corsOk[url]) === true;
  const [strokes, setStrokes] = useState<MatchStroke[]>([]);
  const [tool, setTool] = useState<DrawingTool>('none');
  /** The marker's colours, open beside the rail. */
  const [colorsOpen, setColorsOpen] = useState(false);
  /** Slow motion (decision 102): every panel at ¼x, and back to 1x. */
  const [slow, setSlow] = useState(false);
  const [color, setColor] = useState<DrawColor>('red');
  const [videos, setVideos] = useState<(HTMLVideoElement | null)[]>([null, null]);
  /** Each panel's video shape (width / height), once known: on a phone each panel takes it (decision 133). */
  const [aspects, setAspects] = useState<(number | null)[]>([null, null]);
  // The second panel out of sight (job 2): hidden on a phone's small tile, or scrolled or clipped away.
  // The following side pauses it there and catches up with the coach's state when it shows again; the
  // coach's own keeps playing, since the coach's player is the source both follow.
  const panelEls = useRef<(HTMLDivElement | null)[]>([null, null]);
  const [secondInView, setSecondInView] = useState(true);
  useEffect(() => {
    const el = panelEls.current[1];
    if (!el || typeof IntersectionObserver === 'undefined') { setSecondInView(true); return; }
    const io = new IntersectionObserver(([e]) => setSecondInView(e.isIntersecting && e.intersectionRatio > 0.05), { threshold: [0, 0.05, 0.5] });
    io.observe(el);
    return () => io.disconnect();
  }, [panels.length]);
  const secondHidden = hideSecond || !secondInView;
  const hiddenRef = useRef(false);
  const heldState = useRef<PanelState | null>(null);
  const [moved, setMoved] = useState<{ text: string; at: number } | null>(null);
  const [, setTick] = useState(0);
  const rates = useRef<number[]>([1, 1]);
  const latency = useRef<number | null>(null);
  const ownMoveAt = useRef<number[]>([0, 0]);

  // The panels' video elements, for the drawings' content box. The match's sound is each side's own
  // (decision 129): on by default, and the player's mute is local, never sent.
  useEffect(() => {
    const id = setInterval(() => {
      const els = refs.map((r) => r.current?.getVideoElement() ?? null);
      setVideos((cur) => (cur[0] === els[0] && cur[1] === els[1] ? cur : els));
      const asp = els.map((v) => (v && v.videoWidth && v.videoHeight ? v.videoWidth / v.videoHeight : null));
      setAspects((cur) => (cur[0] === asp[0] && cur[1] === asp[1] ? cur : asp));
    }, 250);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // The moved chip goes after MOVED_CHIP_MS.
  useEffect(() => {
    if (!moved) return;
    const t = setTimeout(() => setTick((n) => n + 1), MOVED_CHIP_MS + 50);
    return () => clearTimeout(t);
  }, [moved]);

  const send = useCallback((m: MatchMessage) => { sendData(m); }, [sendData]);
  const live = useRef({ panels, strokes });
  useEffect(() => { live.current = { panels, strokes }; });
  const stateOf = useCallback((i: Panel): PanelState => {
    const r = refs[i].current;
    return { panel: i, time: r?.getTime() ?? 0, playing: !!r?.isPlaying(), rate: rates.current[i] };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const showMoved = (who: string, t: number) => setMoved({ text: movedChip(who, t), at: Date.now() });

  // ── Coach: the source ────────────────────────────────────────────────────
  const snapshot = useCallback(() => {
    const { panels: ps, strokes: ss } = live.current;
    return msg({ kind: 'open', panels: ps, strokes: ss, states: ps.map((_, i) => stateOf(i as Panel)) });
  }, [stateOf]);
  // Until it stands where the session says, the coach's player sends nothing: the other side would jump.
  const restored = useRef(!initial?.open);
  useEffect(() => {
    if (!isCoach || !restored.current) return;
    send(snapshot());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCoach, panels]);
  useEffect(() => {
    if (!isCoach) return;
    return onDataChannelOpen(() => { if (restored.current) send(snapshot()); });
  }, [isCoach, onDataChannelOpen, send, snapshot]);
  useEffect(() => () => { if (isCoach) send(msg({ kind: 'close' })); }, [isCoach, send]);
  useEffect(() => {
    if (!isCoach) return;
    const id = setInterval(() => { if (restored.current) live.current.panels.forEach((_, i) => send(msg({ kind: 'state', state: stateOf(i as Panel) }))); }, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, [isCoach, send, stateOf]);

  // ── The session's state (decision 130) ──────────────────────────────────
  const chaptersNow = useRef(chapters);
  useEffect(() => { chaptersNow.current = chapters; });
  const warned = useRef(false);
  const persist = useCallback(() => {
    if (!isCoach || !session || !restored.current) return;
    const ps = live.current.panels;
    const states = ps.map((_, i) => stateOf(i as Panel));
    saveMatchSession(session.bookingId, session.sessionId, {
      open: true, panels: ps, states, chapterId: chapterAt(chaptersNow.current, states[0]?.time ?? 0), at: new Date().toISOString(), by: session.by,
    }).catch((e: { code?: string }) => {
      if (!warned.current) { warned.current = true; logger.warn('Match state not saved on the session', { code: e?.code }); }
    });
  }, [isCoach, session, stateOf]);
  useEffect(() => {
    if (!isCoach || !session) return;
    persist();
    const id = setInterval(persist, SESSION_WRITE_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCoach, session?.sessionId, panels]);
  // Opening where the session says: the player takes a seek only once it knows the video's length,
  // so each tick seeks again until every panel stands there (10 s at most), then plays what played.
  useEffect(() => {
    if (!initial?.open) return;
    if (initial.panels.length) setPanels(initial.panels);
    let tries = 0;
    const id = setInterval(() => {
      tries += 1;
      const now = statesNow(initial, Date.now());
      const standing = now.every((st) => {
        const r = refs[st.panel].current;
        if (!r || (r.getVideoElement()?.readyState ?? 0) < 1) return false;
        if (Math.abs(r.getTime() - st.time) < 0.25) return true;
        r.seek(st.time);
        return false;
      });
      if (!standing && tries < 100) return;
      clearInterval(id);
      for (const st of now) {
        const r = refs[st.panel].current;
        if (!r) continue;
        rates.current[st.panel] = st.rate;
        r.setSpeed(st.rate);
        if (st.playing) r.play();
      }
      restored.current = true;
      onRestored?.();
      if (isCoach) { send(snapshot()); persist(); }
    }, 100);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A drawing belongs to the frame it was drawn on: playing clears it.
  const clearOnPlay = () => {
    if (isCoach && live.current.strokes.length) { setStrokes([]); send(msg({ kind: 'clear' })); }
  };

  /** Applies a move to a panel from outside the player (the other side's), never echoed. */
  const apply = (i: Panel, c: MatchControl) => {
    const r = refs[i].current;
    if (!r) return;
    if (c.action === 'seek') r.seek(c.time);
    // The other side's scrub, run through this player's own scrub: whether it
    // pauses, and plays on after, is the async lesson player's rule (one path).
    if (c.action === 'scrub') {
      if (c.phase === 'start') r.scrubStart();
      if (c.phase === 'move') r.scrubTo(c.time);
      if (c.phase === 'end') r.scrubEnd(c.time);
    }
    if (c.action === 'play') { clearOnPlay(); r.play(); }
    if (c.action === 'pause') r.pause();
    if (c.action === 'rate') { rates.current[i] = c.rate; r.setSpeed(c.rate); }
  };

  // A scrub on the wire (job 3, roomMatchSync.scrubSend): at most one point every 100 ms with scrubbing: true,
  // the latest waiting for its slot; the release at once, the exact final position, scrubbing: false.
  const scrubOut = useRef<{ last: number | null; pending: MatchControl | null; timer: ReturnType<typeof setTimeout> | null }[]>([
    { last: null, pending: null, timer: null }, { last: null, pending: null, timer: null },
  ]);
  // The other side's scrub under way on a panel: its heartbeat is left alone meanwhile (scrubHoldsState).
  const remoteScrubAt = useRef<(number | null)[]>([null, null]);
  const sendMove = (i: Panel, c: MatchControl) => {
    if (isCoach) {
      if (c.action === 'seek') send(msg({ kind: 'seek', panel: i, time: c.time, by: 'coach' }));
      else if (c.action === 'scrub') send(msg({ kind: 'scrub', panel: i, phase: c.phase, time: c.time, by: 'coach', scrubbing: c.phase !== 'end' }));
      else send(msg({ kind: 'state', state: stateOf(i) }));
      if (!(c.action === 'scrub' && c.phase !== 'end')) persist();
    } else {
      ownMoveAt.current[i] = Date.now();
      send(msg({ kind: 'ctl', panel: i, control: c.action === 'scrub' ? { ...c, scrubbing: c.phase !== 'end' } : c }));
    }
  };

  /** This side's own move, already applied by the player: say so to the other side. */
  const announce = (i: Panel, c: MatchControl) => {
    if (c.action === 'scrub') {
      const g = scrubOut.current[i];
      if (c.phase === 'move' && scrubSend({ phase: c.phase, lastSentAt: g.last, now: Date.now() }) === 'later') {
        // Kept as the latest, and sent when its slot comes unless the release goes first.
        g.pending = c;
        if (!g.timer) {
          g.timer = setTimeout(() => {
            g.timer = null;
            const p = g.pending;
            g.pending = null;
            if (p) { g.last = Date.now(); sendMove(i, p); }
          }, Math.max(0, (g.last ?? 0) + 100 - Date.now()));
        }
        if (isCoach) return;
        ownMoveAt.current[i] = Date.now();
        return;
      }
      // Going now: an older point still waiting for its slot is dropped, never sent after this one.
      if (g.timer) clearTimeout(g.timer);
      g.timer = null;
      g.pending = null;
      g.last = c.phase === 'end' ? null : Date.now();
    }
    if (c.action === 'rate') rates.current[i] = c.rate;
    if (c.action === 'play') clearOnPlay();
    sendMove(i, c);
  };
  const onUserAction = (i: Panel, a: LessonPreviewAction) => {
    if (a.kind === 'scrubStart') announce(i, { action: 'scrub', phase: 'start', time: refs[i].current?.getTime() ?? 0 });
    if (a.kind === 'scrub') announce(i, { action: 'scrub', phase: 'move', time: a.time });
    if (a.kind === 'scrubEnd') announce(i, { action: 'scrub', phase: 'end', time: a.time });
    if (a.kind === 'seek') announce(i, { action: 'seek', time: a.time });
    if (a.kind === 'play') announce(i, { action: 'play' });
    if (a.kind === 'pause') announce(i, { action: 'pause' });
    if (a.kind === 'rate') announce(i, { action: 'rate', rate: a.rate });
  };

  const coachStroke = useRef<Map<string, number>>(new Map());
  const onStroke = (s: MatchStroke) => {
    setStrokes((cur) => [...cur.filter((x) => x.id !== s.id), s]);
    if (coachStroke.current.has(s.id)) cancelAnimationFrame(coachStroke.current.get(s.id)!);
    coachStroke.current.set(s.id, requestAnimationFrame(() => { coachStroke.current.delete(s.id); send(msg({ kind: 'stroke', stroke: s })); }));
  };
  const erase = (id: string) => {
    if (!live.current.strokes.some((x) => x.id === id)) return;
    setStrokes((cur) => cur.filter((x) => x.id !== id));
    send(msg({ kind: 'unstroke', id }));
  };
  const toggleSlow = () => {
    const rate = slow ? 1 : 0.25;
    setSlow(!slow);
    live.current.panels.forEach((_, i) => {
      refs[i].current?.setSpeed(rate);
      announce(i as Panel, { action: 'rate', rate });
    });
  };
  const undo = () => {
    const last = strokes[strokes.length - 1];
    if (!last) return;
    setStrokes((cur) => cur.filter((x) => x.id !== last.id));
    send(msg({ kind: 'unstroke', id: last.id }));
  };

  // ── The other side's messages ────────────────────────────────────────────
  useEffect(() => onData((_from, data) => {
    if (!isMatchMessage(data)) return;
    if (isCoach) {
      if (data.kind === 'hello' && restored.current) send(snapshot());
      if (data.kind === 'ping') send(msg({ kind: 'pong', id: data.id }));
      // The player's move, applied to the source, and shown as theirs.
      if (data.kind === 'ctl') {
        apply(data.panel, data.control);
        if (!(data.control.action === 'scrub' && data.control.phase !== 'end')) persist();
        if (data.control.action === 'seek' || data.control.action === 'scrub') showMoved(otherFirst, data.control.time);
      }
      return;
    }
    const follow = (st: PanelState) => {
      // Just moved here: the coach's state catches up with it in a moment.
      if (Date.now() - ownMoveAt.current[st.panel] < OWN_MOVE_MS) return;
      const r = refs[st.panel].current;
      if (!r) return;
      // The other side is scrubbing this panel: its points move it, not a heartbeat from before them (job 3).
      if (scrubHoldsState(remoteScrubAt.current[st.panel], Date.now())) return;
      // Out of sight: paused here, its latest state kept for when it shows (job 2).
      if (st.panel === 1 && hiddenRef.current) {
        heldState.current = st;
        if (r.isPlaying()) r.pause();
        return;
      }
      const a = followState({ time: r.getTime(), playing: r.isPlaying() }, st, latency.current ?? 0.03);
      if (a.seekTo !== undefined) r.seek(a.seekTo);
      if (a.play === true) r.play();
      if (a.play === false) r.pause();
      if (rates.current[st.panel] !== a.rate) { rates.current[st.panel] = a.rate; r.setSpeed(a.rate); }
    };
    switch (data.kind) {
      case 'open':
        setPanels(data.panels);
        setStrokes(data.strokes);
        data.states.forEach(follow);
        break;
      case 'state':
        follow(data.state);
        break;
      case 'seek':
        if (Date.now() - ownMoveAt.current[data.panel] < OWN_MOVE_MS && data.by !== 'coach') break;
        apply(data.panel, { action: 'seek', time: data.time });
        if (data.by === 'coach') showMoved(otherFirst, data.time);
        break;
      case 'scrub':
        remoteScrubAt.current[data.panel] = data.phase === 'end' ? null : Date.now();
        apply(data.panel, { action: 'scrub', phase: data.phase, time: data.time });
        if (data.by === 'coach') showMoved(otherFirst, data.time);
        break;
      case 'stroke': setStrokes((cur) => [...cur.filter((x) => x.id !== data.stroke.id), data.stroke]); break;
      case 'unstroke': setStrokes((cur) => cur.filter((x) => x.id !== data.id)); break;
      case 'clear': setStrokes([]); break;
      case 'pong': {
        const sent = pings.current.get(data.id);
        if (sent !== undefined) { latency.current = nextLatency(latency.current, performance.now() - sent); pings.current.delete(data.id); }
        break;
      }
      default: break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [onData, isCoach, send, snapshot, otherFirst]);

  const pings = useRef(new Map<number, number>());
  useEffect(() => {
    if (isCoach) return;
    send(msg({ kind: 'hello' }));
    let id = 0;
    const timer = setInterval(() => {
      id += 1;
      pings.current.set(id, performance.now());
      send(msg({ kind: 'ping', id }));
    }, 2000);
    return () => clearInterval(timer);
  }, [isCoach, send]);

  const [comparing, setComparing] = useState(false);
  const pickComparison = (src: ClipSource) => {
    setComparing(false);
    if (src.isCameraSource || src.videoUrl.startsWith('blob:')) {
      showError(`${playerName ?? 'Your player'} can't see a video from this device. Compare with the match or the library.`);
      return;
    }
    setPanels((cur) => [cur[0], { url: src.videoUrl, label: src.label ?? src.segment?.label ?? 'Comparison' }]);
    if (src.segment) setTimeout(() => { refs[1].current?.seek(src.segment!.startTime); announce(1, { action: 'seek', time: src.segment!.startTime }); }, 600);
  };

  // A saved moment's side-by-side (145, 149), the coach's from its marker or its row: its clip beside the
  // match as Side by Side puts one (75), the match at the moment and the clip at its own start; again, closed.
  const toggleMomentSideBySide = (c: LiveChapter) => {
    const clip = sideBySideClip(c, videoUrl);
    if (!clip) return;
    if (panels[1]?.url === clip.url) { setPanels((cur) => [cur[0]]); return; }
    setPanels((cur) => [cur[0], { url: clip.url, label: clip.label }]);
    refs[0].current?.seek(c.startTime);
    announce(0, { action: 'seek', time: c.startTime });
    setTimeout(() => { refs[1].current?.seek(clip.start); announce(1, { action: 'seek', time: clip.start }); }, 600);
  };

  const sideBySide = panels.length > 1;
  const onShapeRef = useRef(onShape);
  useEffect(() => { onShapeRef.current = onShape; });
  useEffect(() => { onShapeRef.current?.({ aspect: aspects[0], sideBySide }); }, [aspects, sideBySide]);
  // Hidden: the second panel paused at once on the following side; shown again: the coach's latest state.
  useEffect(() => {
    hiddenRef.current = sideBySide && secondHidden && !isCoach;
    const r = refs[1].current;
    if (!r || isCoach || !sideBySide) return;
    if (secondHidden) { if (r.isPlaying()) r.pause(); return; }
    const st = heldState.current;
    heldState.current = null;
    if (st) {
      const a = followState({ time: r.getTime(), playing: r.isPlaying() }, st, latency.current ?? 0.03);
      if (a.seekTo !== undefined) r.seek(a.seekTo);
      if (a.play === true) r.play();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondHidden, sideBySide, isCoach]);
  // No drawing from the column's small tile, where the tools are not shown (144).
  const drawTool = tools && (tool === 'brush' || tool === 'arrow') ? tool : null;
  const movedShown = moved && Date.now() - moved.at < MOVED_CHIP_MS ? moved.text : null;
  const toolBtn = (on: boolean) => `ios-press flex h-11 w-11 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 ${on ? 'bg-white text-[#202124]' : 'text-white'}`;

  return (
    <div className={`absolute inset-0 z-10 flex bg-call-bezel ${phone ? 'flex-col justify-center gap-px' : 'flex-row gap-2 p-2'}`}
      data-room-match={isCoach ? 'coach' : 'player'}>
      {panels.map((p, i) => (
        // On a phone each panel is its video's own shape, centred, so the player's bar sits on the
        // video rather than under a letterbox (decision 133); elsewhere the panels share the stage.
        <div key={`${i}-${p.url}`} ref={(el) => { panelEls.current[i] = el; }}
          className={`relative flex min-h-0 min-w-0 overflow-hidden bg-black ${phone && aspects[i] ? 'w-full flex-none' : 'flex-1'} ${phone ? '' : 'rounded-[12px]'} ${i === 1 && hideSecond ? 'hidden' : ''}`}
          style={phone && aspects[i] ? { aspectRatio: String(aspects[i]), maxHeight: `${100 / panels.length}%` } : undefined}
          data-match-panel={i} data-panel-hidden={i === 1 && secondHidden ? '' : undefined}>
          <ChapteredMatch
            ref={refs[i]}
            videoUrl={p.url}
            label={p.label}
            panel={i}
            crossOrigin={loadsWithCors(p.url) ? 'anonymous' : undefined}
            fill
            // The chapters are the match's: the first panel's, never the comparison's.
            chapters={i === 0 ? chapters : null}
            stageFullscreen={stageFullscreen}
            sideBySide={i === 0 ? { openUrl: panels[1]?.url ?? null, matchUrl: videoUrl, onToggle: isCoach ? toggleMomentSideBySide : undefined } : undefined}
            canFlag={isCoach && i === 0}
            coachFirst={coachFirst}
            onChaptersChange={onChaptersChange}
            chaptersOpenAtStart={!phone}
            // A column beside the picture with the stage to itself; an overlay side by side or on a phone.
            chaptersColumn={!phone && !sideBySide}
            // The coach's Close sits over the column's corner: the column starts under it.
            // Full height, with its one close at its top (163): no Close the match over its corner any more.
            chaptersTopInset={0}
            onFlagPause={() => announce(i as Panel, { action: 'pause' })}
            onUserAction={(a) => onUserAction(i as Panel, a)}
            overlay={(
              <RoomMatchDrawLayer panel={i as Panel} video={videos[i]} strokes={strokes.filter((s) => s.panel === i)}
                tool={isCoach ? drawTool : null} color={color} onStroke={isCoach ? onStroke : undefined}
                erasing={isCoach && tools && tool === 'eraser'} onErase={isCoach ? erase : undefined} />
            )}
          />
        </div>
      ))}

      {/* "Ana moved to 4:12" when the other side moves it. */}
      {movedShown && (
        <div className="pointer-events-none absolute left-1/2 top-3 z-[5] -translate-x-1/2">
          <span role="status" className="call-glass rounded-full px-3 py-1 text-[13px] leading-[18px] font-medium text-white" data-moved-chip>{movedShown}</span>
        </div>
      )}

      {/* The coach's tools, a rail on the left edge (decision 102): the marker (its colour dot is the
          marker; a tap picks it and opens the colours), the arrow, the eraser, Undo, Side by Side and
          slow motion. No circle and no lock. */}
      {isCoach && tools && (
        <div className={`absolute left-3 top-1/2 z-[6] flex -translate-y-1/2 items-start gap-2 transition-opacity duration-200 ${chromeHidden ? 'pointer-events-none opacity-0' : ''}`}>
          <div className="call-glass flex flex-col items-center gap-1 rounded-full p-1" role="toolbar" aria-label="Tools" data-tools-rail>
            <button type="button" aria-label="Marker" title="Marker" aria-pressed={tool === 'brush'} aria-expanded={colorsOpen}
              onClick={() => { setTool('brush'); setColorsOpen((o) => (tool === 'brush' ? !o : true)); }} className={toolBtn(tool === 'brush')} data-tool="marker">
              <span className={`h-5 w-5 rounded-full ${tool === 'brush' ? 'ring-2 ring-[#202124]/30' : 'border-2 border-white'}`} style={{ background: COLOR_HEX[color] }} aria-hidden="true" />
            </button>
            <button type="button" aria-label="Arrow" title="Arrow" aria-pressed={tool === 'arrow'}
              onClick={() => { setColorsOpen(false); setTool((cur) => (cur === 'arrow' ? 'none' : 'arrow')); }} className={toolBtn(tool === 'arrow')} data-tool="arrow">
              <ArrowUpRight size={20} weight="bold" aria-hidden="true" />
            </button>
            <button type="button" aria-label="Eraser" title="Eraser" aria-pressed={tool === 'eraser'}
              onClick={() => { setColorsOpen(false); setTool((cur) => (cur === 'eraser' ? 'none' : 'eraser')); }} className={toolBtn(tool === 'eraser')} data-tool="eraser">
              <Eraser size={20} weight="bold" aria-hidden="true" />
            </button>
            <button type="button" aria-label="Undo" title="Undo" disabled={!strokes.length}
              onClick={undo} className={`${toolBtn(false)} disabled:opacity-40`} data-tool="undo">
              <ArrowCounterClockwise size={20} weight="bold" aria-hidden="true" />
            </button>
            <button type="button" onClick={() => (sideBySide ? setPanels((cur) => [cur[0]]) : setComparing(true))}
              aria-pressed={sideBySide} aria-label={sideBySide ? 'One Video' : 'Side by Side'} title={sideBySide ? 'One Video' : 'Side by Side'}
              className={toolBtn(sideBySide)} data-tool="side-by-side">
              <Columns size={20} weight="bold" aria-hidden="true" />
            </button>
            <button type="button" aria-label="Slow motion" title="Slow motion" aria-pressed={slow}
              onClick={toggleSlow} className={toolBtn(slow)} data-tool="slow-motion">
              <Gauge size={20} weight="bold" aria-hidden="true" />
            </button>
          </div>
          {colorsOpen && tool === 'brush' && (
            <div className="call-glass flex flex-col gap-1 rounded-full p-1" role="radiogroup" aria-label="Marker colour" data-marker-colors>
              {DRAW_COLORS.map((c) => (
                <button key={c.value} type="button" role="radio" aria-checked={color === c.value} aria-label={c.label}
                  onClick={() => { setColor(c.value); setColorsOpen(false); }}
                  className="ios-press flex h-11 w-11 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40">
                  <span className={`h-6 w-6 rounded-full ${color === c.value ? 'ring-2 ring-white ring-offset-2 ring-offset-[#202124]' : ''}`} style={{ background: COLOR_HEX[c.value] }} aria-hidden="true" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Top right: Hide cameras, a small one beside the tiles (decision 131), and the coach's Close. */}
      {cameras && (
        <div className="absolute right-3 top-3 z-[5] flex items-center gap-2">
          {cameras && (
            <button type="button" onClick={cameras.onToggle} data-cameras-control aria-pressed={cameras.hidden}
              aria-label={cameras.hidden ? 'Show cameras' : 'Hide cameras'} title={cameras.hidden ? 'Show cameras' : 'Hide cameras'}
              className="call-glass ios-press flex h-9 w-9 items-center justify-center rounded-full text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40">
              <SidebarSimple size={18} weight={cameras.hidden ? 'regular' : 'fill'} aria-hidden="true" />
            </button>
          )}
          {/* No X on the match (163): it opens and closes from its own bar button, Open Match and Close Match. */}
        </div>
      )}

      {comparing && (
        <AddComparisonSourceModal isOpen videoUrl={videoUrl} deviceSources={false}
          primarySource={{ videoUrl, label: playerName ?? 'Match' }}
          notableSegments={(chapters ?? []).map((c): NotableSegment => ({ id: c.id, label: c.label, startTime: c.startTime, endTime: c.endTime, ...(c.note ? { note: c.note } : {}) }))}
          onConfirm={pickComparison} onClose={() => setComparing(false)} />
      )}
    </div>
  );
}
